import { FC, useEffect, useRef, useState } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { NavigationItem } from '@telialt/models/src/Navigation';
import { usePrevious } from '@telialt/utils/src/hooks';

import { NAVIGATION_TEST_IDS } from '../../test-ids';
import {
    MobileMenuContainer,
    MobileMenuScrollContainer,
    MobileMenuWrapper,
    OverscrollShadow,
} from './MobileMenu.styles';
import { MobileMenuItem } from './MobileMenuItem';
import { MobileMenuTitle } from './MobileMenuTitle';

type Props = {
    isVisible?: boolean;
    items: NavigationItem[];
};

export const MobileMenu: FC<Props> = ({ isVisible, items }) => {
    const { t } = useTranslations();

    const [selectedItemsStack, setSelectedItemsStack] = useState<NavigationItem[]>([]);
    const [hasOverscrollTop, setHasOverscrollTop] = useState<boolean>(false);
    const [hasOverscrollBottom, setHasOverscrollBottom] = useState<boolean>(false);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const scrollerNode = scrollContainerRef.current;
        const updateScrollState = () => {
            if (scrollerNode && scrollerNode.scrollHeight > scrollerNode.clientHeight) {
                const scrollTop = scrollerNode.scrollTop || 0;
                const scrollBottom = Math.round(scrollerNode.scrollHeight - (scrollerNode.clientHeight + scrollTop));

                setHasOverscrollTop(scrollTop > 0);
                setHasOverscrollBottom(scrollBottom > 0);
            } else {
                setHasOverscrollTop(false);
                setHasOverscrollBottom(false);
            }
        };

        updateScrollState();
        scrollerNode?.addEventListener('scroll', updateScrollState);
        window.addEventListener('resize', updateScrollState);

        return () => {
            scrollerNode?.removeEventListener('scroll', updateScrollState);
            window.removeEventListener('resize', updateScrollState);
        };
    }, []);

    const handleItemSelect = (item: NavigationItem) => {
        setSelectedItemsStack((prev) => [...prev, item]);
    };

    const handleBackButtonClick = () => {
        setSelectedItemsStack((prev) => prev.slice(0, -1));
    };

    const selectedItem = selectedItemsStack[selectedItemsStack.length - 1];
    const displayedItems = selectedItem?.children || items;
    const previousSelectedItem = usePrevious(selectedItem);

    useEffect(() => {
        if (isVisible && (selectedItem !== previousSelectedItem || document.activeElement === document.body)) {
            const firstFocusableElement = wrapperRef.current?.querySelector('a[href], button') as HTMLElement;
            firstFocusableElement?.focus();
        }
    }, [isVisible, previousSelectedItem, selectedItem]);

    return (
        <MobileMenuWrapper ref={wrapperRef}>
            <MobileMenuTitle selectedItemTitle={selectedItem?.title} onBackClick={handleBackButtonClick} />
            <MobileMenuContainer>
                <MobileMenuScrollContainer
                    ref={scrollContainerRef}
                    data-test={NAVIGATION_TEST_IDS.MOBILE_MENU}
                    role="menu"
                    aria-label={selectedItem?.title || t['navbar.burger.aria_label']}
                    data-section="Menu"
                >
                    {displayedItems.map((item, index) => (
                        <MobileMenuItem
                            key={`${item.title}-${index}`}
                            item={item}
                            menuLevel={selectedItemsStack.length + 1}
                            onOpenChildrenClick={() => handleItemSelect(item)}
                        />
                    ))}
                </MobileMenuScrollContainer>
                <OverscrollShadow $isTop={true} $isVisible={hasOverscrollTop} />
                <OverscrollShadow $isTop={false} $isVisible={hasOverscrollBottom} />
            </MobileMenuContainer>
        </MobileMenuWrapper>
    );
};
